import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import UserBadge from '../common/UserBadge';
import { formatDistanceToNow } from 'date-fns';
import { FiMessageSquare, FiClock, FiTrash2, FiArchive, FiArrowRight } from 'react-icons/fi';

interface ChatSession {
  id: string;
  persona_id: string;
  persona_name: string;
  message_count: number;
  last_message?: string;
  last_activity: string;
  created_at: string;
  updated_at: string;
  status?: 'active' | 'idle' | 'archived';
  persona_avatar?: string;
  persona_specialization?: string;
  owner?: { id: string; display_name: string; avatar_url?: string; role?: string } | null;
}

interface ChatSessionCardProps {
  session: ChatSession;
  onDelete?: (sessionId: string) => void;
  onArchive?: (sessionId: string) => void;
  onClick?: (sessionId: string) => void;
}

const STATUS_PILL: Record<string, { bg: string; text: string; label: string; pulse?: boolean }> = {
  active: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Active', pulse: true },
  idle: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Idle' },
  archived: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Archived' },
};

function relTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Recently';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return 'Recently'; }
}

/** Extract a topic line from the last message or persona specialization */
function extractTopic(session: ChatSession): string {
  if (session.last_message) {
    const msg = session.last_message.replace(/^(system|user|assistant):\s*/i, '');
    return msg.length > 200 ? msg.slice(0, 197) + '...' : msg;
  }
  return session.persona_specialization || 'Chat session';
}

export const ChatSessionCard: React.FC<ChatSessionCardProps> = ({
  session, onDelete, onArchive, onClick
}) => {
  const { darkMode } = useTheme();
  const [hovered, setHovered] = useState(false);
  const statusInfo = STATUS_PILL[session.status || 'active'] || STATUS_PILL.active;
  const topic = extractTopic(session);

  return (
    <div
      className={`rounded-lg shadow-lg border-l-4 p-5 cursor-pointer transition-all duration-200 hover:translate-y-[-1px] ${
        darkMode
          ? 'bg-gray-800 border-emerald-600 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]'
          : 'bg-white border-emerald-500 shadow-sm hover:shadow-md'
      }`}
      onClick={() => onClick?.(session.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header: PersonaBadge + name + status pill + time */}
      <div className="flex items-center gap-3 mb-3">
        <PersonaBadge
          personaId={session.persona_id}
          personaName={session.persona_name}
          size="md"
          clickable={false}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {session.persona_name.replace(/_/g, ' ')}
            </span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${statusInfo.bg} ${statusInfo.text}`}>
              {statusInfo.pulse && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse mr-1" />}
              {statusInfo.label}
            </span>
          </div>
          {session.persona_specialization && (
            <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {session.persona_specialization}
            </p>
          )}
          {session.owner !== undefined && (
            <div className="mt-0.5">
              <UserBadge owner={session.owner} size="sm" />
            </div>
          )}
        </div>
        <span className={`text-xs shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {relTime(session.last_activity)}
        </span>
      </div>

      {/* Topic / last message preview */}
      <div className={`text-sm mb-3 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {topic}
      </div>

      <style>{`.line-clamp-3{display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}`}</style>

      {/* Metrics row + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <FiMessageSquare className="w-3 h-3" /> {session.message_count}
          </span>
          <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <FiClock className="w-3 h-3" /> {relTime(session.created_at)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {hovered && onArchive && (
            <button onClick={(e) => { e.stopPropagation(); onArchive(session.id); }}
              className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
              <FiArchive className="w-3.5 h-3.5" />
            </button>
          )}
          {hovered && onDelete && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
              className={`p-1 rounded ${darkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500'}`}>
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <Link
            to={session.persona_id ? `/chat/persona/${session.persona_id}/${session.id}` : '/chat/sessions'}
            onClick={(e) => e.stopPropagation()}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
              darkMode
                ? 'text-emerald-400 hover:bg-emerald-900/20'
                : 'text-emerald-600 hover:bg-emerald-50'
            }`}
          >
            Continue <FiArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};
